"use client";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import StoreCard from "@/components/ui/StoreCard";
import { apiFetch } from "@/utils/api";
import ErrorState from "@/components/ui/ErrorState";
import EmptyState from "@/components/ui/EmptyState";
import Image from "next/image";
import Link from "next/link";
import { useInView } from "react-intersection-observer";
import Carousel from "@/components/ui/Carousel";
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from "@/utils/icons";
import { ChevronDown, ChevronUp, Check } from "lucide-react";

const ALPHABETS: string[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "az", label: "A-Z" },
  { value: "za", label: "Z-A" },
  { value: "most_coupons", label: "Most Coupons" },
  { value: "most_offers", label: "Most Offers" },
];

type Category = { _id: string; name: string; slug: string };

type Store = {
  _id?: string;
  name: string;
  slug: string;
  icon?: string;
  media_link?: string;
  description?: string;
  website?: string;
  categories?: string[];
  category?: string; // Legacy field
  isActive?: boolean;
  coupons?: number;
  offersCount?: number;
};

type ApiResponse = {
  success: boolean;
  data: Store[];
};

// Custom MobileSelectDropdown Component 
interface Option {
  label: string;
  value: string;
}

interface MobileSelectDropdownProps {
  label: string;
  options: Option[];
  currentValue: string;
  onChange: (newValue: string) => void;
  // Dynamic options for categories
  dynamicOptions?: Category[];
}

const MobileSelectDropdown: React.FC<MobileSelectDropdownProps> = ({
  label,
  options,
  currentValue,
  onChange,
  dynamicOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const allOptions = useMemo(() => {
    let combined = [...options];
    if (dynamicOptions) {
      combined = combined.concat(dynamicOptions.map(cat => ({ value: cat.slug, label: cat.name })));
    }
    return combined;
  }, [options, dynamicOptions]);

  // Close the dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    onChange(value);
    setIsOpen(false);
  };

  const selectedOption = allOptions.find((o) => o.value === currentValue) || allOptions[0];

  return (
    <div className="relative flex-1" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-1 px-3 py-2 border-2 border-gray-200 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 w-full"
      >
        <div className="flex items-center">
          <span className="truncate">{selectedOption?.label || label}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
      </button>

      {isOpen && (
        <div
          // Custom dropdown overlay positioning
          className="absolute z-40 top-full mt-1 w-full min-w-40 rounded-lg shadow-xl bg-white border border-gray-200 left-0"
        >
          <div className="py-1 max-h-60 overflow-y-auto">
            {allOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-2 text-sm flex justify-between items-center transition-colors duration-150 ${currentValue === option.value
                  ? "bg-primary-50 text-primary-600 font-medium"
                  : "hover:bg-gray-100 text-gray-700"
                  }`}
              >
                {option.label}
                {currentValue === option.value && (
                  <Check className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function StoresPage() {
  const PAGE_SIZE = 24;

  // State management
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [stores, setStores] = useState<Store[]>([]);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [trending, setTrending] = useState<Store[]>([]);
  const [stats, setStats] = useState({ merchants: 0, offers: 0 });
  const [categories, setCategories] = useState<Category[]>([]);

  // Filter states
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [alpha, setAlpha] = useState("");
  const [sort, setSort] = useState("az");

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [lastVerified, setLastVerified] = useState<string>("");

  const mobileStoresRef = useRef<HTMLDivElement>(null);
  const { ref: loadMoreRef, inView } = useInView({ triggerOnce: false });

  // Load initial data only once 
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load categories
        const categoriesResponse = await apiFetch<{
          success: boolean;
          data: Category[];
        }>("/categories/");
        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data);
        }

        // Load trending stores
        const trendingResponse = await apiFetch<{
          success: boolean;
          data: Store[];
        }>("/stores/?trending=true&isActive=true&isFeatured=true");
        if (trendingResponse.success) {
          setTrending(trendingResponse.data);
        }

        // Load all stores for client-side filtering
        const allStoresResponse = await apiFetch<ApiResponse>("/stores/?isActive=true");
        if (allStoresResponse.success) {
          setAllStores(allStoresResponse.data);
          setStores(allStoresResponse.data);

          // Calculate stats
          setStats({
            merchants: allStoresResponse.data.length,
            offers: allStoresResponse.data.reduce(
              (sum, s) => sum + (s.offersCount || 0),
              0
            ),
          });
        }

        // Set last verified date
        setLastVerified(
          new Date().toLocaleDateString("en-IN", {
            weekday: "short",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        );
      } catch (err) {
        console.error("Failed to load initial data:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load stores";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Client-side filtering and sorting
  const filteredStores = useMemo(() => {
    let filtered = allStores.filter((store) => {
      // Search filter
      const matchesSearch =
        !search ||
        store.name.toLowerCase().includes(search.toLowerCase()) ||
        (store.description &&
          store.description.toLowerCase().includes(search.toLowerCase()));

      // Category filter - check if category is in categories array or matches legacy category field
      const matchesCategory = !category || 
        (store.categories && store.categories.includes(category)) || 
        store.category === category;

      // Alphabet filter
      const matchesAlpha =
        !alpha || store.name.charAt(0).toUpperCase() === alpha;

      return matchesSearch && matchesCategory && matchesAlpha;
    });

    // Apply sorting
    switch (sort) {
      case "za":
        filtered = filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "most_coupons":
        filtered = filtered.sort((a, b) => (b.coupons || 0) - (a.coupons || 0));
        break;
      case "most_offers":
        filtered = filtered.sort(
          (a, b) => (b.offersCount || 0) - (a.offersCount || 0)
        );
        break;
      default: // "az"
        filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return filtered;
  }, [allStores, search, category, alpha, sort]);

  // Paginated results for display 
  const paginatedStores = useMemo(() => {
    const endIndex = page * PAGE_SIZE;
    return filteredStores.slice(0, endIndex);
  }, [filteredStores, page]);

  // Update hasMore based on pagination
  useEffect(() => {
    setHasMore(paginatedStores.length < filteredStores.length);
  }, [paginatedStores.length, filteredStores.length]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, category, alpha, sort]);

  // Load more stores when scrolling 
  useEffect(() => {
    if (!hasMore || isFetchingMore || loading || !inView) return;

    setIsFetchingMore(true);
    // Simulate loading delay for smooth UX
    setTimeout(() => {
      setPage((prev) => prev + 1);
      setIsFetchingMore(false);
    }, 300);
  }, [hasMore, isFetchingMore, loading, inView]);

  // Handle retry
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    // Reload page to retry
    window.location.reload();
  }, []);


  const scrollMobileStores = (direction: 'left' | 'right') => {
    if (mobileStoresRef.current) {
      const scrollAmount = 300;
      const currentScroll = mobileStoresRef.current.scrollLeft;

      if (direction === 'left') {
        mobileStoresRef.current.scroll({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        mobileStoresRef.current.scroll({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  // Loading skeleton and other render functions 
  const renderLoadingSkeleton = () => (
    <div className="flex flex-col md:flex-row gap-6 max-w-7xl mx-auto py-8 px-2">
      <aside className="w-full md:w-64 flex-shrink-0 bg-white rounded-xl shadow p-6 mb-6 md:mb-0 animate-pulse">
        <div className="h-6 bg-gray-200 rounded mb-4 w-2/3 mx-auto" />
        <div className="h-4 bg-gray-200 rounded mb-2 w-1/2 mx-auto" />
        <div className="h-8 bg-gray-200 rounded mb-2 w-3/4 mx-auto" />
        <div className="h-4 bg-gray-200 rounded mb-2 w-1/2 mx-auto" />
        <div className="h-8 bg-gray-200 rounded mb-2 w-3/4 mx-auto" />
        <div className="h-3 bg-gray-100 rounded mb-2 w-1/3 mx-auto" />
        <div className="h-4 bg-gray-100 rounded mb-2 w-2/3 mx-auto" />
        <div className="h-4 bg-gray-100 rounded mb-2 w-2/3 mx-auto" />
      </aside>
      <main className="flex-1">
        <div className="h-8 w-1/2 bg-gray-200 rounded mb-6" />
        <div className="mb-8">
          <div className="font-semibold text-gray-200 text-lg mb-3">Trending Merchants</div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="min-w-[220px] bg-white rounded-xl shadow flex flex-col items-center px-4 py-3 border border-gray-100 animate-pulse"
              >
                <div className="w-16 h-16 bg-gray-200 rounded-lg mb-2" />
                <div className="h-4 w-24 bg-gray-200 rounded mb-1" />
                <div className="h-3 w-20 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6">
          <div className="flex-1 h-10 bg-gray-100 rounded-full animate-pulse" />
          <div className="flex gap-2">
            <div className="w-32 h-10 bg-gray-100 rounded-lg animate-pulse" />
            <div className="w-32 h-10 bg-gray-100 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className="w-12 h-8 bg-gray-100 rounded-full animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow flex flex-col items-center px-4 py-6 border border-gray-100 animate-pulse"
            >
              <div className="w-16 h-16 bg-gray-200 rounded-lg mb-3" />
              <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-16 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );

  const renderLoadingCards = () =>
    Array.from({ length: 6 }, (_, i) => (
      <div
        key={`skeleton-${i}`}
        className="bg-white rounded-xl shadow flex flex-col items-center px-4 py-6 border border-gray-100 animate-pulse"
      >
        <div className="w-16 h-16 bg-gray-200 rounded-lg mb-3" />
        <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-16 bg-gray-100 rounded" />
      </div>
    ));

  if (loading) return renderLoadingSkeleton();

  if (error) {
    return <ErrorState message={error} onRetry={handleRetry} />;
  }

  return (
    <div className="bg-gray-50 pt-0 lg:pb-3">
      <div className="md:block hidden bg-gradient-to-r from-primary-600 to-purple-700 text-white relative h-80 sm:h-72 lg:h-84 md:h-96 md:-mt-24">
      </div>

      <div className="max-w-7xl mx-auto lg:p-8 md:p-5 py-4 px-2 overflow-x-hidden pt-0 -mt-0 lg:-mt-60 md:-mt-66 relative z-10">
        <div className="lg:block hidden w-full mb-3 px-4 sm:px-0 text-white relative z-20">
          <nav className="flex" aria-label="Breadcrumb">
            <ol role="list" className="flex items-center space-x-1 text-xs">
              <li>
                <Link href="/" className="font-medium">
                  Home
                </Link>

              </li>
              {true && (
                <svg
                  className="flex-shrink-0 w-3 h-3 ml-2 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"></path>
                </svg>
              )}
              <li>
                <span className="text-gray-400"></span>
              </li>
              <li>
                <span>
                  Stores
                </span>
              </li>
            </ol>
          </nav>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          <aside className="w-full md:w-64 flex-shrink-0">

            {/* Mobile Stats Card (Visible only on mobile) */}
            <div className="md:hidden flex-col w-full bg-primary-100 rounded-xl mt-4 shadow p-4 border border-primary-100 flex items-center justify-center overflow-hidden">
              <div className="w-full flex items-center justify-between">
                <div className="flex flex-col items-start pr-4">
                  <div className="text-xl font-bold text-gray-700 mb-1">
                    All Merchants
                  </div>
                  <div className="text-sm text-gray-700">Total Merchants: <span className="font-bold">{filteredStores.length.toLocaleString()}</span> of {stats.merchants.toLocaleString()}</div>
                  <div className="text-sm text-gray-700">Total Offers: <span className="font-bold">{stats.offers.toLocaleString()}</span></div>
                  <div className="text-xs mt-1 flex items-center gap-1 text-green-600">
                    <CheckCircleIcon className="w-4 h-4" /> Verified On: {lastVerified}
                  </div>
                  <div className="w-full h-0.5 bg-green-700 mt-1 rounded-full" />

                </div>
                {/* Store Icon */}
                <div className="w-20 h-20 flex-shrink-0 bg-slate-50 rounded-xl shadow flex items-center justify-center">
                  <Image
                    src={"/assets/storeiconpng.png"}
                    alt={"All Merchants"}
                    width={64}
                    height={64}
                    className="object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Desktop Sidebar (Hidden on mobile, visible from md breakpoint) */}
            <aside className="hidden md:block w-full bg-white rounded-xl shadow p-6 mb-6 md:mb-0 border border-primary-100">
              <div className="text-center mb-6 text-gray-700">
                <div className="w-24 h-24 mx-auto bg-slate-50 rounded-full shadow flex items-center justify-center mb-3">
                  <Image
                    src={"/assets/storeiconpng.png"}
                    alt={"All Merchants"}
                    width={80}
                    height={80}
                    className="object-contain"
                  />
                </div>
                <div className="text-lg font-bold text-center mb-1">
                  All Merchants
                </div>
                <div className="text-xs text-gray-500 mb-4">
                  {stats.merchants.toLocaleString()} Stores Total
                </div>
              </div>

              <div className="w-full h-0.5 bg-green-700 mb-6 rounded-full" />

              <nav className="flex flex-col gap-2 text-sm text-gray-700">
                <Link className="hover:text-primary-600 font-medium" href="/category">
                  See All Categories
                </Link>
                <Link className="hover:text-primary-600 font-medium" href="/store">
                  See All Stores
                </Link>
                <Link className="hover:text-primary-600 font-medium" href="/offers">
                  See All Offers
                </Link>
                  {/* <Link className="hover:text-primary-600 font-medium" href="/cashback">
                  See All CashBack
                </Link>
                <Link className="hover:text-primary-600 font-medium" href="/bank-wallet-offers">
                  See All Banks
                </Link> */}
              </nav>

              <div className="mt-8 w-full flex justify-center">
                {/* Crousal Banner Card*/}
                <div className="w-full">
                  <Carousel orientation="vertical" />
                </div>
              </div>
            </aside>
          </aside>

          {/* Main Content Area  */}
          <main className="flex-1 px-2 md:px-0 overflow-x-hidden">
            <div className="inline-block">
              <h1 className="lg:text-4xl text-2xl font-extrabold md:font-bold md:text-white lg:text-white">
                Stores
              </h1>
            </div>
            <div className="md:text-white/80 mb-6 text-sm md:text-base">
              Who doesn&apos;t love a great deal? {process.env.NEXT_PUBLIC_APP_TITLE} brings you the best
              discounts from top stores like Amazon, Flipkart, Myntra, Nykaa,
              Swiggy, Domino&apos;s and more. From shopping for the latest trends to
              catching a movie to grabbing your favorite pizza, we&apos;ve got the
              best deals waiting for you. No more scrolling endlessly for
              discounts—just pick your store, grab a promo code, and enjoy the
              savings.
            </div>

            {/* Trending Merchants*/}
            <section className="mb-8 lg:pt-10 md:pt-12">
              <div className="mb-4 lg:mb-6 flex items-center justify-between">
                <div className="text-lg font-semibold mb-3">Trending Merchants</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => scrollMobileStores('left')}
                    className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
                    aria-label="Scroll Offers Left"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => scrollMobileStores('right')}
                    className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
                    aria-label="Scroll Offers Right"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="relative group">
                <div
                  ref={mobileStoresRef}
                  className="flex gap-5 overflow-x-auto pb-2 scrollbar-hide max-w-full"
                  style={{ scrollBehavior: "smooth" }}
                >
                  {trending.map((store) => (
                    <Link
                      key={store._id || store.slug}
                      href={`/store/${store.slug}`}
                      className="min-w-[130px] sm:min-w-[160px] md:min-w-[200px] rounded-xl shadow-md flex flex-col items-center md:py-2 border border-gray-200 hover:shadow-lg hover:scale-[102%] transition-all duration-200 bg-white"
                    >
                      <Image
                        src={
                          store.media_link ||
                          "/assets/OfferCodeFlight.jpg"
                        }
                        alt={store.name}
                        width={56}
                        height={56}
                        className="md:w-32 md:h-14 h-12 md:mb-2 rounded-lg object-contain"
                        onError={(e) => {
                          e.currentTarget.src = "/assets/OfferCodeFlight.jpg";
                        }}
                      />
                      <div className="w-full h-0.5 bg-gray-200 md:mt-1 rounded-full" />

                      <div className="font-bold lg:text-sm text-xs mb-1 text-center truncate w-full px-1 mt-2">{store.name}</div>
                      <div className="lg:text-xs text-[10px] mb-1 whitespace-nowrap">
                        {store.coupons || 0} Coupons | {(Number(store.offersCount) - Number(store.coupons)) || 0} Offers
                      </div>
                    </Link>
                  ))}
                </div>

              </div>
            </section>

            <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6 text-gray-700">
              <div className="flex-1 w-full">
                <label htmlFor="store-search" className="sr-only">
                  Search stores
                </label>
                <div className="relative">
                  <input
                    id="store-search"
                    type="text"
                    placeholder="Search stores..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-1.5 border-2 border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-300 text-base transition-all pr-10"
                    autoComplete="off"
                    aria-label="Search stores"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              <div className="flex gap-2 w-full md:w-auto">
                {/* 1. Category Dropdown - Conditional Rendering */}
                <div className="w-1/2 md:w-auto">
                  {/* Mobile Custom Dropdown */}
                  <div className="md:hidden">
                    <MobileSelectDropdown
                      label="All Categories"
                      options={[{ value: "", label: "All Categories" }]}
                      dynamicOptions={categories}
                      currentValue={category}
                      onChange={setCategory}
                    />
                  </div>

                  {/* Desktop Native Select  */}
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="hidden md:block px-3 py-2 rounded-lg border-2 border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 w-1/2 md:w-auto text-sm"
                    aria-label="Filter by category"
                  >
                    <option value="">All Categories</option>
                    {categories?.map((cat) => (
                      <option key={cat._id} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 2. Sort Dropdown - Conditional Rendering */}
                <div className="w-1/2 md:w-auto">
                  {/* Mobile Custom Dropdown */}
                  <div className="md:hidden">
                    <MobileSelectDropdown
                      label="Sort"
                      options={SORT_OPTIONS}
                      currentValue={sort}
                      onChange={setSort}
                    />
                  </div>

                  {/* Desktop Native Select (Original Code) */}
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="hidden md:block px-3 py-2 rounded-lg border-2 border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 w-1/2 md:w-auto text-sm"
                    aria-label="Sort stores"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Alphabet Filter (Scrollable on mobile) */}
            <div className="flex text-[10px] md:text-sm lg:text-sm xl:text-sm flex-wrap gap-1 mb-6 overflow-x-auto max-w-full">
              <button
                className={`flex-shrink-0 px-1 md:px-2 lg:px-2 py-1 transition-all ${!alpha
                  ? "text-primary-600"
                  : "hover:bg-primary-50"
                  }`}
                onClick={() => setAlpha("")}
              >
                All
              </button>
              {ALPHABETS.map((a) => (
                <button
                  key={a}
                  className={`flex-shrink-0 px-1 lg:px-2 md:px-2 transition-all ${alpha === a
                    ? "text-primary-600"
                    : "hover:bg-primary-50"
                    }`}
                  onClick={() => setAlpha(a)}
                >
                  {a}
                </button>
              ))}
            </div>

            {/* Results count */}
            {(search || category || alpha || sort !== "az") && (
              <div className="text-sm text-white mb-4 px-1">
                Showing {filteredStores.length} of {stats.merchants} stores
              </div>
            )}

            {/* Results Grid - Optimized for all screen sizes */}
            {paginatedStores.length === 0 ? (
              <EmptyState message="No stores found." emoji="🏬" />
            ) : (
              <div
                role="list"
                className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4"
              >
                {paginatedStores.map((store, i) => (
                  <div
                    key={store._id || `store-${i}`}
                    tabIndex={0}
                    aria-label={`${store.name} store`}
                    role="listitem"
                    className="focus:outline-none focus:ring-2 focus:ring-white rounded-xl w-full"
                  >
                    <StoreCard
                      name={store.name}
                      logo={
                        store.icon ||
                        store.media_link ||
                        "/assets/OfferCodeFlight.jpg"
                      }
                      offers={Number(store.offersCount) - Number(store.coupons) || 0}
                      coupons={store.coupons || 0}
                      slug={store.slug || String(store._id || i)}
                    />
                  </div>
                ))}
                {isFetchingMore && renderLoadingCards()}
              </div>
            )}

            {/* Load more indicator */}
            {hasMore && !isFetchingMore && (
              <div ref={loadMoreRef} className="text-center py-6">
                <span className="text-primary-200">Scroll to load more...</span>
              </div>
            )}

            {isFetchingMore && (
              <div className="text-center py-6">
                <span className="animate-pulse text-white">
                  Loading more stores...
                </span>
              </div>
            )}

            {!hasMore && paginatedStores.length > 0 && (
              <div className="text-center py-4 text-gray-500">
                You&apos;ve reached the end of the results!
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}